import { SearchOutlined } from '@ant-design/icons';
import { Input, Space, Table, Typography } from 'antd';
import { useSearchParams } from 'react-router-dom';

import {
  Error403Unauthorized,
  MultipleSelect,
  SearchButtons,
} from '@app/components';
import { EmployeeSetupHintModal } from '@app/components/Modals/EmployeeSetupHintModal/EmployeeSetupHintModal';
import { useEmployeeSetupHintModal } from '@app/components/Modals/EmployeeSetupHintModal/hooks/useEmployeeSetupHintModal';
import {
  PREFIX,
  STATUSES,
  USER_TABLE_HEADER,
  USERS_EDIT_PATH,
} from '@app/constants';
import {
  adaptDataToSelect,
  getPaginationItemRenderWithDataTestIds,
} from '@app/utils';
import '@pages/Users/UsersList/styles/Users.desktop.scss';
import '@pages/Users/UsersList/styles/Users.tablet.scss';
import '@pages/Users/UsersList/styles/Users.mobile.scss';

import { adaptDataToTable, useUsers } from '@pages/Users/UsersList';

const { Text } = Typography;

export const UsersList = () => {
  const {
    searchQuery,
    searchQueryError,
    isLoader,
    ref,
    totalRowsCount,
    pageNumber,
    data,
    rowsPerPage,
    allBranches,
    selectedBranches,
    positions,
    selectedPositions,
    selectedStatuses,
    setSelectedStatuses,
    setSelectedPositions,
    setSelectedBranches,
    showData,
    resetAll,
    handlePageChange,
    isMobile,
    contextHolder,
    handleEdit,
    handleHistoryIconClick,
    hasUserRightToUserRegistry,
    hasUserRightToEdit,
    onChangeSearchInput,
  } = useUsers();

  const [searchParams] = useSearchParams();
  const employeeId = searchParams.get('employeeId');

  const { isHintModalOpen, closeHintModal } =
    useEmployeeSetupHintModal(employeeId);

  if (!hasUserRightToUserRegistry) {
    return <Error403Unauthorized />;
  }

  return (
    <div className={`${PREFIX}users`}>
      {contextHolder}
      <Space className="full-width" direction="vertical" size="large">
        <Input
          id="test-users-list-search-input"
          className="user-input full-width"
          ref={ref}
          addonBefore={<SearchOutlined />}
          placeholder="Поиск (по имени, телефону, email)"
          value={searchQuery}
          onChange={onChangeSearchInput}
          data-testid="search-users"
        />
        {searchQueryError && <Text type="danger">{searchQueryError}</Text>}

        <Space
          align="end"
          size="large"
          direction="horizontal"
          wrap
          className="toolbar-space full-width"
        >
          <Space
            size="large"
            direction="horizontal"
            wrap
            className="selector-space full-width"
          >
            <MultipleSelect
              className="user-selector"
              placeholder="Все филиалы"
              allOptions={adaptDataToSelect(allBranches)}
              width={220}
              selectedOption={selectedBranches}
              setSelectedOptions={setSelectedBranches}
              defaultSelected={true}
              dataTestId="active-branch-select"
              optionDataTestId="branch-item"
            />
            <MultipleSelect
              className="user-selector"
              placeholder="Все должности"
              allOptions={adaptDataToSelect(positions)}
              width={220}
              selectedOption={selectedPositions}
              setSelectedOptions={setSelectedPositions}
              dataTestId="position-select"
              optionDataTestId="position-item"
            />
            <MultipleSelect
              className="user-selector"
              placeholder="Все статусы"
              allOptions={STATUSES}
              width={220}
              selectedOption={selectedStatuses}
              setSelectedOptions={setSelectedStatuses}
              dataTestId="status-select"
              filterName="filterName"
            />

            <SearchButtons
              className="tablet-buttons"
              resetAll={resetAll}
              showData={() => showData()}
            />
          </Space>
          <SearchButtons
            className="desktop-buttons"
            resetAll={resetAll}
            showData={() => showData()}
          />
        </Space>
        <div className="table-container">
          {data ? (
            <Table
              rowClassName={'user-item'}
              columns={USER_TABLE_HEADER}
              dataSource={adaptDataToTable({
                data,
                handleEdit,
                handleHistoryIconClick,
                hasUserRightToEdit,
              })}
              loading={isLoader}
              pagination={{
                itemRender: getPaginationItemRenderWithDataTestIds,
                size: isMobile ? 'small' : 'default',
                showSizeChanger: true,
                total: totalRowsCount,
                defaultCurrent: pageNumber,
                current: pageNumber,
                pageSize: rowsPerPage,
                position: isMobile ? ['bottomCenter'] : ['bottomLeft'],
                onChange: handlePageChange,
                showTotal: (total, range) => (
                  <span data-testid="pagination-info">{`Показаны ${range[0]}-${range[1]} из ${total}`}</span>
                ),
              }}
            />
          ) : (
            <Text>Нет данных</Text>
          )}
        </div>
      </Space>

      {employeeId && (
        <EmployeeSetupHintModal
          isOpen={isHintModalOpen}
          onClose={closeHintModal}
          accessRightsPath={`${USERS_EDIT_PATH}/?id=${employeeId}&key=3`}
        />
      )}
    </div>
  );
};
